import React, { useState, useEffect, useMemo, useRef } from 'react';
import Navbar from '../components/storefront/Navbar';
import Footer from '../components/storefront/Footer';
import supabase from '../supabase/supabase';
import { useStore } from '../context/StoreContext';
import { useNavigate } from 'react-router-dom';
import { FiPackage, FiMapPin, FiUser, FiLogOut, FiPlus, FiTrash2, FiRefreshCw, FiChevronRight, FiSearch, FiExternalLink, FiCopy, FiCheck, FiAlertTriangle, FiClock, FiHome, FiBriefcase, FiChevronDown, FiEdit3 } from 'react-icons/fi';
import { SearchableCountrySelect } from '../components/ui/SearchableCountrySelect';
import SafeIcon from '../common/SafeIcon';
import LoadingSpinner from '../common/LoadingSpinner';

const ORDER_TABS = [
  { id: 'all', label: 'All' },
  { id: 'recent', label: 'Recent' },
  { id: 'to_pay', label: 'To Pay' },
  { id: 'to_ship', label: 'To Ship' },
  { id: 'to_receive', label: 'To Receive' },
  { id: 'completed', label: 'Completed' },
  { id: 'cancelled', label: 'Cancelled' }
];

const ADDRESS_TITLES = ['Home', 'Office', 'Other'];

const Profile = () => {
  const { user, profile, checkUser, clearCart, restoreCart, shippingDistricts } = useStore();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('orders');
  const [orderSubTab, setOrderSubTab] = useState('all');
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [copiedId, setCopiedId] = useState(null);

  const [showAddressForm, setShowAddressForm] = useState(false);
  const [customTitle, setCustomTitle] = useState('');
  const [newAddress, setNewAddress] = useState({
    title: 'Home',
    firstName: '',
    lastName: '',
    addressLine1: '',
    addressLine2: '',
    streetName: '',
    unitNumber: '',
    city: '',
    district: '',
    countryCode: '+94',
    phone: ''
  });

  // District search for address form
  const [districtSearch, setDistrictSearch] = useState('');
  const [districtDropdownOpen, setDistrictDropdownOpen] = useState(false);
  const districtRef = useRef(null);

  const [profileData, setProfileData] = useState({ full_name: '', gender: '', phone_number: '' });
  const [updatingProfile, setUpdatingProfile] = useState(false);

  // Account deletion state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
    } else {
      fetchOrders();
      if (profile) {
        setProfileData({
          full_name: profile.full_name || '',
          gender: profile.gender || '',
          phone_number: profile.phone_number || ''
        });
      }
    }
  }, [user, profile, navigate]);

  useEffect(() => {
    const handleClick = (e) => {
      if (districtRef.current && !districtRef.current.contains(e.target)) {
        setDistrictDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const fetchOrders = async () => {
    setLoadingOrders(true);
    if (!user) return;
    const { data } = await supabase
      .from('orders')
      .select('*, order_items(*, products(name, image))')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (data) setOrders(data);
    setLoadingOrders(false);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    clearCart();
    navigate('/');
  };

  const handleSaveAddress = async (e) => {
    e.preventDefault();
    const currentAddresses = profile?.saved_addresses || [];
    const finalTitle = newAddress.title === 'Other' ? (customTitle.trim() || 'Other') : newAddress.title;
    const updatedAddresses = [...currentAddresses, { ...newAddress, title: finalTitle, id: Date.now().toString() }];
    
    const { error } = await supabase
      .from('profiles')
      .update({ saved_addresses: updatedAddresses })
      .eq('id', user.id);

    if (!error) {
      setShowAddressForm(false);
      setNewAddress({
        title: 'Home',
        firstName: '',
        lastName: '',
        addressLine1: '',
        addressLine2: '',
        streetName: '',
        unitNumber: '',
        city: '',
        district: '',
        countryCode: '+94',
        phone: ''
      });
      setDistrictSearch('');
      setCustomTitle('');
      await checkUser();
    }
  };

  const handleDeleteAddress = async (id) => {
    const updatedAddresses = profile.saved_addresses.filter(addr => addr.id !== id);
    const { error } = await supabase
      .from('profiles')
      .update({ saved_addresses: updatedAddresses })
      .eq('id', user.id);

    if (!error) {
      await checkUser();
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setUpdatingProfile(true);
    const { error } = await supabase
      .from('profiles')
      .update(profileData)
      .eq('id', user.id);

    if (!error) {
      alert('Profile updated successfully');
      await checkUser();
    }
    setUpdatingProfile(false);
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== user?.email) {
      alert('The email you typed does not match your account email.');
      return;
    }

    setDeletingAccount(true);
    try {
      const { error } = await supabase.rpc('delete_own_account');
      if (error) throw error;

      await supabase.auth.signOut();
      clearCart();
      alert('Your account has been permanently deleted.');
      navigate('/');
    } catch (err) {
      alert('Failed to delete account: ' + err.message);
    } finally {
      setDeletingAccount(false);
    }
  };

  const handleResumeOrder = (order) => {
    if (order.order_snapshot && order.order_snapshot.items) {
      restoreCart(order.order_snapshot.items);
      navigate('/checkout');
    } else {
      alert("Unable to restore items for this draft.");
    }
  };

  const handleCancelOrder = async (id) => {
    if (window.confirm("Are you sure you want to cancel this order? This action cannot be undone.")) {
      const { error } = await supabase
        .from('orders')
        .update({ status: 'cancelled' })
        .eq('id', id);
      
      if (!error) {
        fetchOrders();
      } else {
        alert("Failed to cancel order: " + error.message);
      }
    }
  };

  const handleCopyTrackingLink = (orderId) => {
    const link = `${window.location.origin}/track/${orderId}`;
    navigator.clipboard.writeText(link);
    setCopiedId(orderId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const filteredOrders = useMemo(() => {
    const twoWeeksAgo = new Date();
    twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);

    return orders.filter(order => {
      const s = order.status.toLowerCase();
      const p = order.payment_status?.toLowerCase();
      
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const orderShortId = order.id.split('-')[0].toLowerCase();
        const productNames = order.order_items?.map(i => i.products?.name?.toLowerCase()).join(' ') || '';
        const matchesSearch = orderShortId.includes(q) || productNames.includes(q) || order.id.toLowerCase().includes(q);
        if (!matchesSearch) return false;
      }
      
      switch (orderSubTab) {
        case 'recent':
          return new Date(order.created_at) >= twoWeeksAgo;
        case 'to_pay': 
          return (s === 'draft' || s === 'pending') && p !== 'paid';
        case 'to_ship': 
          return (s === 'confirmed' || s === 'packed') || (p === 'paid' && s === 'pending');
        case 'to_receive': 
          return s === 'shipped';
        case 'completed': 
          return s === 'delivered';
        case 'cancelled': 
          return s === 'cancelled' || s === 'returned';
        default: 
          return true;
      }
    });
  }, [orders, orderSubTab, searchQuery]);

  const filteredDistricts = shippingDistricts.filter(d =>
    d.name.toLowerCase().includes(districtSearch.toLowerCase())
  );

  const handleSelectDistrict = (district) => {
    setNewAddress(prev => ({ ...prev, district: district.name }));
    setDistrictSearch(district.name);
    setDistrictDropdownOpen(false);
  };

  if (!user || !profile) return <div className="min-h-screen bg-brand-sand pt-24"><Navbar /></div>;

  return (
    <div className="bg-brand-sand min-h-screen font-sans flex flex-col pt-24">
      <Navbar />
      <div className="flex-1 max-w-7xl mx-auto px-6 py-12 w-full">
        <div className="flex flex-col lg:flex-row gap-8">
          
          {/* Sidebar */}
          <div className="w-full lg:w-80 flex-shrink-0">
            <div className="bg-white/90 p-8 rounded-3xl border border-white shadow-xl sticky top-28">
              <div className="flex items-center gap-5 mb-10">
                <div className="w-16 h-16 bg-gradient-to-br from-brand-darkSage to-brand-sage text-white rounded-2xl flex items-center justify-center font-serif text-2xl font-bold shadow-lg">
                  {profile.full_name?.charAt(0) || 'U'}
                </div>
                <div>
                  <h2 className="font-bold text-brand-darkSage text-lg leading-tight">{profile.full_name}</h2>
                  <p className="text-xs text-brand-lightText font-medium mt-1 truncate max-w-[180px]">{user.email}</p>
                </div>
              </div>
              
              <nav className="flex flex-col gap-2">
                <TabButton active={activeTab === 'orders'} onClick={() => setActiveTab('orders')} icon={FiPackage} label="Order History" count={orders.length} />
                <TabButton active={activeTab === 'addresses'} onClick={() => setActiveTab('addresses')} icon={FiMapPin} label="Saved Addresses" count={profile.saved_addresses?.length || 0} />
                <TabButton active={activeTab === 'profile'} onClick={() => setActiveTab('profile')} icon={FiUser} label="Account Settings" />
                
                <div className="pt-6 mt-6 border-t border-brand-darkSage/5">
                  <button 
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-5 py-4 rounded-2xl text-sm font-bold text-red-500 hover:bg-red-50 transition-all group"
                  >
                    <SafeIcon icon={FiLogOut} size={20} /> Sign Out
                  </button>
                </div>
              </nav>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="flex-1 min-w-0">
            
            {activeTab === 'orders' && (
              <div className="space-y-6">
                <div className="bg-white/90 rounded-3xl border border-white shadow-xl p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
                    <h2 className="text-3xl font-serif text-brand-darkSage">Your Orders</h2>
                    <div className="bg-brand-sand px-4 py-2 rounded-full text-xs font-bold text-brand-darkSage border border-brand-darkSage/5">
                      {orders.length} {orders.length === 1 ? 'order' : 'orders'}
                    </div>
                  </div>
                  
                  <div className="relative">
                    <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-lightText" size={18} />
                    <input
                      type="text"
                      placeholder="Search by order ID, product name..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-12 pr-4 py-3.5 bg-brand-sand/50 border-2 border-brand-darkSage/5 rounded-2xl text-sm text-brand-darkSage placeholder:text-brand-lightText/60 focus:border-brand-gold focus:bg-white outline-none transition-all"
                    />
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery('')}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-brand-lightText hover:text-brand-darkSage text-xs font-bold"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>
                
                <div className="flex gap-2 overflow-x-auto pb-2 no-scrollbar">
                  {ORDER_TABS.map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setOrderSubTab(tab.id)}
                      className={`whitespace-nowrap px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all duration-300 border-2 ${
                        orderSubTab === tab.id 
                          ? 'bg-brand-darkSage text-white border-brand-darkSage shadow-lg' 
                          : 'bg-white/80 text-brand-darkSage/60 hover:text-brand-darkSage hover:bg-white border-white'
                      }`}
                    >
                      {tab.id === 'recent' && <SafeIcon icon={FiClock} size={10} className="inline mr-1" />}
                      {tab.label}
                    </button>
                  ))}
                </div>

                {orderSubTab === 'recent' && !searchQuery && (
                  <div className="bg-brand-gold/5 border border-brand-gold/20 rounded-xl p-3 flex items-center gap-2">
                    <SafeIcon icon={FiClock} className="text-brand-gold" size={14} />
                    <p className="text-xs text-brand-darkSage">Showing orders from the last 2 weeks</p>
                  </div>
                )}

                {loadingOrders ? (
                  <div className="flex flex-col items-center justify-center py-24">
                    <LoadingSpinner size="lg" text="Loading your orders..." />
                  </div>
                ) : filteredOrders.length === 0 ? (
                  <div className="bg-white/60 p-16 rounded-3xl border border-white shadow-xl text-center">
                    <div className="w-20 h-20 bg-brand-gold/10 text-brand-gold rounded-full flex items-center justify-center mx-auto mb-6">
                      <SafeIcon icon={FiPackage} size={32} />
                    </div>
                    <h3 className="text-2xl font-serif text-brand-darkSage mb-2">
                      {searchQuery ? 'No matching orders' : `No ${orderSubTab.replace('_', ' ')} orders`}
                    </h3>
                    <p className="text-sm text-brand-lightText mb-8 max-w-xs mx-auto">
                      {searchQuery ? 'Try a different search term.' : orderSubTab === 'recent' ? "You haven't placed any orders in the last 2 weeks." : "You haven't placed any orders matching this status yet."}
                    </p>
                    {!searchQuery && (
                      <button onClick={() => navigate('/shop')} className="bg-brand-darkSage text-white px-10 py-4 rounded-full text-xs font-bold uppercase tracking-widest hover:bg-brand-gold transition-all shadow-lg">
                        Explore Our Collection
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredOrders.map(order => (
                      <OrderCard
                        key={order.id}
                        order={order}
                        onResume={handleResumeOrder}
                        onCancel={handleCancelOrder}
                        onCopyLink={handleCopyTrackingLink}
                        onViewTracking={() => navigate(`/track/${order.id}`)}
                        copiedId={copiedId}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'addresses' && (
              <div className="space-y-8">
                <div className="flex justify-between items-center">
                  <h2 className="text-3xl font-serif text-brand-darkSage">Addresses</h2>
                  {!showAddressForm && (
                    <button onClick={() => setShowAddressForm(true)} className="flex items-center gap-2 bg-brand-darkSage text-white px-6 py-3 rounded-full text-[11px] font-black uppercase tracking-widest hover:bg-brand-gold transition-all shadow-lg">
                      <SafeIcon icon={FiPlus} size={16} /> Add New
                    </button>
                  )}
                </div>

                {showAddressForm && (
                  <form onSubmit={handleSaveAddress} className="bg-white/90 p-10 rounded-3xl border border-white shadow-2xl space-y-6">
                    <h3 className="font-serif text-2xl text-brand-darkSage mb-2">New Shipping Address</h3>
                    
                    {/* Title Selection */}
                    <div>
                      <label className="block text-[10px] font-black text-brand-darkSage uppercase tracking-widest mb-2">Address Title</label>
                      <div className="flex flex-wrap gap-3">
                        {ADDRESS_TITLES.map(title => (
                          <button
                            key={title}
                            type="button"
                            onClick={() => { setNewAddress({ ...newAddress, title }); if (title !== 'Other') setCustomTitle(''); }}
                            className={`flex items-center gap-2 px-5 py-3 rounded-xl text-xs font-bold uppercase tracking-widest border-2 transition-all ${
                              newAddress.title === title
                                ? 'bg-brand-darkSage text-white border-brand-darkSage'
                                : 'bg-white text-brand-darkSage/60 border-brand-darkSage/10 hover:border-brand-gold/50'
                            }`}
                          >
                            {title === 'Home' && <SafeIcon icon={FiHome} size={14} />}
                            {title === 'Office' && <SafeIcon icon={FiBriefcase} size={14} />}
                            {title === 'Other' && <SafeIcon icon={FiEdit3} size={14} />}
                            {title}
                          </button>
                        ))}
                      </div>
                      {newAddress.title === 'Other' && (
                        <div className="mt-3">
                          <input
                            type="text"
                            value={customTitle}
                            onChange={(e) => setCustomTitle(e.target.value)}
                            placeholder="Enter custom title (e.g. Parents, Warehouse)"
                            className="w-full border-2 border-brand-darkSage/10 rounded-xl px-4 py-3 bg-white focus:border-brand-gold outline-none transition-colors text-sm"
                            maxLength={30}
                          />
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-[10px] font-black text-brand-darkSage uppercase tracking-widest mb-2">First Name *</label>
                        <input required type="text" value={newAddress.firstName} onChange={e => setNewAddress({...newAddress, firstName: e.target.value})} className="w-full border-2 border-brand-darkSage/5 rounded-2xl px-5 py-4 bg-white focus:border-brand-gold outline-none transition-colors" />
                      </div>
                      <div>
                        <label className="block text-[10px] font-black text-brand-darkSage uppercase tracking-widest mb-2">Last Name *</label>
                        <input required type="text" value={newAddress.lastName} onChange={e => setNewAddress({...newAddress, lastName: e.target.value})} className="w-full border-2 border-brand-darkSage/5 rounded-2xl px-5 py-4 bg-white focus:border-brand-gold outline-none transition-colors" />
                      </div>
                    </div>

                    <div>
                      <label className="block text-[10px] font-black text-brand-darkSage uppercase tracking-widest mb-2">Address Line 1 *</label>
                      <input required type="text" value={newAddress.addressLine1} onChange={e => setNewAddress({...newAddress, addressLine1: e.target.value})} className="w-full border-2 border-brand-darkSage/5 rounded-2xl px-5 py-4 bg-white focus:border-brand-gold outline-none transition-colors" placeholder="House/Building number and street" />
                    </div>

                    <div>
                      <label className="block text-[10px] font-black text-brand-darkSage uppercase tracking-widest mb-2">Address Line 2</label>
                      <input type="text" value={newAddress.addressLine2} onChange={e => setNewAddress({...newAddress, addressLine2: e.target.value})} className="w-full border-2 border-brand-darkSage/5 rounded-2xl px-5 py-4 bg-white focus:border-brand-gold outline-none transition-colors" placeholder="Apartment, suite, floor, etc. (optional)" />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-[10px] font-black text-brand-darkSage uppercase tracking-widest mb-2">Street Name *</label>
                        <input required type="text" value={newAddress.streetName} onChange={e => setNewAddress({...newAddress, streetName: e.target.value})} className="w-full border-2 border-brand-darkSage/5 rounded-2xl px-5 py-4 bg-white focus:border-brand-gold outline-none transition-colors" placeholder="Street / Road name" />
                      </div>
                      <div>
                        <label className="block text-[10px] font-black text-brand-darkSage uppercase tracking-widest mb-2">Unit Number</label>
                        <input type="text" value={newAddress.unitNumber} onChange={e => setNewAddress({...newAddress, unitNumber: e.target.value})} className="w-full border-2 border-brand-darkSage/5 rounded-2xl px-5 py-4 bg-white focus:border-brand-gold outline-none transition-colors" placeholder="Unit / Apt # (optional)" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-[10px] font-black text-brand-darkSage uppercase tracking-widest mb-2">Town / City *</label>
                        <input required type="text" value={newAddress.city} onChange={e => setNewAddress({...newAddress, city: e.target.value})} className="w-full border-2 border-brand-darkSage/5 rounded-2xl px-5 py-4 bg-white focus:border-brand-gold outline-none transition-colors" />
                      </div>
                      {shippingDistricts.length > 0 && (
                        <div ref={districtRef} className="relative">
                          <label className="block text-[10px] font-black text-brand-darkSage uppercase tracking-widest mb-2">District *</label>
                          <div className="relative">
                            <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-lightText/50 pointer-events-none" size={14} />
                            <input
                              required
                              type="text"
                              value={districtSearch}
                              onChange={(e) => {
                                setDistrictSearch(e.target.value);
                                setDistrictDropdownOpen(true);
                                if (!e.target.value) {
                                  setNewAddress(prev => ({ ...prev, district: '' }));
                                }
                              }}
                              onFocus={() => setDistrictDropdownOpen(true)}
                              className="w-full border-2 border-brand-darkSage/5 rounded-2xl pl-10 pr-10 py-4 bg-white focus:border-brand-gold outline-none transition-colors"
                              placeholder="Search district..."
                            />
                            <FiChevronDown
                              className={`absolute right-4 top-1/2 -translate-y-1/2 text-brand-lightText/50 transition-transform cursor-pointer ${districtDropdownOpen ? 'rotate-180' : ''}`}
                              size={16}
                              onClick={() => setDistrictDropdownOpen(!districtDropdownOpen)}
                            />
                          </div>
                          {districtDropdownOpen && (
                            <div className="absolute z-50 w-full mt-1 bg-white border-2 border-brand-darkSage/10 rounded-2xl shadow-xl max-h-48 overflow-y-auto">
                              {filteredDistricts.length === 0 ? (
                                <div className="px-4 py-3 text-xs text-gray-400 text-center">No districts found</div>
                              ) : (
                                filteredDistricts.map(d => (
                                  <button
                                    key={d.id}
                                    type="button"
                                    onClick={() => handleSelectDistrict(d)}
                                    className={`w-full text-left px-4 py-3 text-sm flex justify-between items-center hover:bg-brand-gold/5 transition-colors ${
                                      newAddress.district === d.name ? 'bg-brand-gold/10 text-brand-darkSage font-bold' : 'text-brand-darkSage'
                                    }`}
                                  >
                                    <span>{d.name}</span>
                                    <span className="text-xs text-brand-gold font-bold">Rs. {Number(d.delivery_fee).toFixed(2)}</span>
                                  </button>
                                ))
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-[10px] font-black text-brand-darkSage uppercase tracking-widest mb-2">Phone *</label>
                      <div className="flex gap-3">
                        <SearchableCountrySelect value={newAddress.countryCode} onChange={(e) => setNewAddress({...newAddress, countryCode: e.target.value})} />
                        <input required type="tel" value={newAddress.phone} onChange={e => setNewAddress({...newAddress, phone: e.target.value})} className="flex-1 border-2 border-brand-darkSage/5 rounded-2xl px-5 py-4 bg-white focus:border-brand-gold outline-none transition-colors" />
                      </div>
                    </div>

                    <div className="flex gap-4 pt-6">
                      <button type="submit" className="flex-1 bg-brand-darkSage text-white py-4 rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-brand-gold transition-all shadow-xl">
                        Save Address
                      </button>
                      <button type="button" onClick={() => { setShowAddressForm(false); setDistrictSearch(''); setCustomTitle(''); }} className="flex-1 bg-gray-100 text-gray-500 py-4 rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-gray-200 transition-all">
                        Cancel
                      </button>
                    </div>
                  </form>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {profile.saved_addresses?.map(addr => (
                    <div key={addr.id} className="bg-white/80 p-8 rounded-3xl border border-white shadow-xl relative group hover:shadow-2xl transition-all duration-500">
                      <button onClick={() => handleDeleteAddress(addr.id)} className="absolute top-6 right-6 text-gray-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100">
                        <SafeIcon icon={FiTrash2} size={20} />
                      </button>
                      <div className="flex items-center gap-3 mb-6 text-brand-darkSage">
                        <div className="w-10 h-10 bg-brand-gold/10 rounded-xl flex items-center justify-center text-brand-gold">
                          {addr.title === 'Office' ? <SafeIcon icon={FiBriefcase} size={20} /> : <SafeIcon icon={FiHome} size={20} />}
                        </div>
                        <div>
                          <span className="font-bold text-lg">{addr.firstName} {addr.lastName}</span>
                          <span className="block text-[10px] font-black uppercase tracking-widest text-brand-gold mt-0.5">{addr.title || 'Home'}</span>
                        </div>
                      </div>
                      <p className="text-sm text-brand-lightText font-medium mb-1">{addr.addressLine1 || addr.address}</p>
                      {addr.addressLine2 && <p className="text-sm text-brand-lightText font-medium mb-1">{addr.addressLine2}</p>}
                      {addr.streetName && <p className="text-sm text-brand-lightText font-medium mb-1">{addr.streetName}</p>}
                      {addr.unitNumber && <p className="text-sm text-brand-lightText font-medium mb-1">Unit: {addr.unitNumber}</p>}
                      <p className="text-sm text-brand-lightText font-medium mb-4">{addr.city}{addr.district ? `, ${addr.district}` : ''}</p>
                      <div className="inline-block bg-brand-sand px-3 py-1.5 rounded-lg text-xs font-mono font-bold text-brand-darkSage border border-brand-darkSage/5">
                        {addr.countryCode} {addr.phone}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'profile' && (
              <div className="space-y-8">
                <h2 className="text-3xl font-serif text-brand-darkSage">Account Settings</h2>
                <form onSubmit={handleUpdateProfile} className="bg-white/80 p-10 rounded-3xl border border-white shadow-xl space-y-6 max-w-xl">
                  <div>
                    <label className="block text-[10px] font-black text-brand-darkSage uppercase tracking-widest mb-2">Full Name</label>
                    <input required type="text" value={profileData.full_name} onChange={e => setProfileData({...profileData, full_name: e.target.value})} className="w-full border-2 border-brand-darkSage/5 rounded-2xl px-5 py-4 bg-white focus:border-brand-gold outline-none transition-colors" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-[10px] font-black text-brand-darkSage uppercase tracking-widest mb-2">Gender</label>
                      <select required value={profileData.gender} onChange={e => setProfileData({...profileData, gender: e.target.value})} className="w-full border-2 border-brand-darkSage/5 rounded-2xl px-5 py-4 bg-white focus:border-brand-gold outline-none transition-colors appearance-none">
                        <option value="">Select</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] font-black text-brand-darkSage uppercase tracking-widest mb-2">Phone</label>
                      <input required type="tel" value={profileData.phone_number} onChange={e => setProfileData({...profileData, phone_number: e.target.value})} className="w-full border-2 border-brand-darkSage/5 rounded-2xl px-5 py-4 bg-white focus:border-brand-gold outline-none transition-colors" placeholder="+94..." />
                    </div>
                  </div>
                  <button disabled={updatingProfile} type="submit" className="w-full bg-brand-darkSage text-white py-4 rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-brand-gold transition-all shadow-xl mt-4 disabled:opacity-50">
                    {updatingProfile ? 'Saving...' : 'Save Changes'}
                  </button>
                </form>

                {/* Delete Account Section */}
                <div className="bg-red-50/80 p-10 rounded-3xl border border-red-200 shadow-xl max-w-xl">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                      <SafeIcon icon={FiAlertTriangle} className="text-red-500" size={20} />
                    </div>
                    <div>
                      <h3 className="font-bold text-red-800 text-lg">Delete Account</h3>
                      <p className="text-xs text-red-600">This action is permanent and cannot be undone</p>
                    </div>
                  </div>

                  <p className="text-sm text-red-700 mb-6 leading-relaxed">
                    Deleting your account will permanently remove your profile, order history, and saved addresses. 
                    This action <strong>cannot be reversed</strong>.
                  </p>

                  {!showDeleteConfirm ? (
                    <button
                      type="button"
                      onClick={() => setShowDeleteConfirm(true)}
                      className="bg-red-500 text-white px-6 py-3 rounded-xl text-[11px] font-black uppercase tracking-widest hover:bg-red-600 transition-all"
                    >
                      Delete My Account
                    </button>
                  ) : (
                    <div className="space-y-4">
                      <div className="bg-white p-5 rounded-2xl border border-red-200">
                        <p className="text-xs font-bold text-red-800 uppercase tracking-widest mb-2">
                          Type your email to confirm
                        </p>
                        <p className="text-sm text-red-600 mb-3 font-mono bg-red-50 px-3 py-2 rounded-lg inline-block">
                          {user.email}
                        </p>
                        <input
                          type="text"
                          value={deleteConfirmText}
                          onChange={(e) => setDeleteConfirmText(e.target.value)}
                          placeholder="Type your email address here"
                          className="w-full border-2 border-red-300 rounded-xl px-4 py-3 text-sm bg-white focus:border-red-500 outline-none transition-colors"
                          autoComplete="off"
                        />
                      </div>
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={handleDeleteAccount}
                          disabled={deletingAccount || deleteConfirmText !== user.email}
                          className="bg-red-500 text-white px-6 py-3 rounded-xl text-[11px] font-black uppercase tracking-widest hover:bg-red-600 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          {deletingAccount ? 'Deleting...' : 'Permanently Delete Account'}
                        </button>
                        <button
                          type="button"
                          onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText(''); }}
                          className="bg-gray-200 text-gray-600 px-6 py-3 rounded-xl text-[11px] font-black uppercase tracking-widest hover:bg-gray-300 transition-all"
                        >
                          Cancel
                        </button>
                      </div>
                      {deleteConfirmText !== user.email && deleteConfirmText.length > 0 && (
                        <p className="text-xs text-red-500 font-semibold">
                          Email does not match. Please type <strong>{user.email}</strong> exactly.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

const OrderCard = ({ order, onResume, onCancel, onCopyLink, onViewTracking, copiedId }) => {
  const isUnpaid = (order.status === 'draft' || order.status === 'pending') && order.payment_status !== 'paid';
  const trackingLink = `${window.location.origin}/track/${order.id}`;

  return (
    <div className="bg-white/90 rounded-3xl border border-white shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300">
      <div className="px-6 py-5 border-b border-brand-darkSage/5 bg-gradient-to-r from-white/80 to-brand-sand/20 flex flex-wrap justify-between gap-4 items-center">
        <div className="flex items-center gap-4">
          <div className="w-11 h-11 bg-brand-sand rounded-xl flex items-center justify-center text-brand-darkSage font-mono font-bold text-[10px] border border-brand-darkSage/5">
            #{order.id.split('-')[0].toUpperCase().slice(0, 6)}
          </div>
          <div>
            <p className="text-[9px] font-black uppercase tracking-[0.2em] text-brand-gold mb-0.5">
              {new Date(order.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </p>
            <p className="text-sm font-bold text-brand-darkSage">Rs. {order.total_amount.toFixed(2)}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <span className={`px-3 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest border-2 ${
            order.status === 'delivered' ? 'bg-green-50 text-green-700 border-green-100' :
            order.status === 'cancelled' ? 'bg-red-50 text-red-700 border-red-100' :
            order.status === 'draft' ? 'bg-gray-100 text-gray-500 border-gray-200' :
            order.status === 'shipped' ? 'bg-purple-50 text-purple-700 border-purple-100' :
            'bg-brand-gold/5 text-brand-gold border-brand-gold/10'
          }`}>
            {order.status === 'draft' ? 'Payment Pending' : order.status}
          </span>
        </div>
      </div>
      
      <div className="px-6 py-5">
        <div className="space-y-3">
          {order.order_items?.map(item => (
            <div key={item.id} className="flex items-center gap-4">
              <div className="w-14 h-14 bg-brand-sand/30 rounded-xl p-2 border border-brand-darkSage/5 flex-shrink-0">
                <img src={item.products?.image} alt={item.products?.name} className="w-full h-full object-contain" loading="lazy" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-bold text-brand-darkSage text-sm truncate">{item.products?.name}</h4>
                <p className="text-xs text-brand-lightText">Qty: {item.quantity} × Rs. {item.price.toFixed(2)}</p>
              </div>
              <p className="font-bold text-brand-darkSage text-sm">Rs. {(item.price * item.quantity).toFixed(2)}</p>
            </div>
          ))}
        </div>

        {!isUnpaid && order.status !== 'draft' && (
          <div className="mt-5 p-4 bg-brand-sand/50 rounded-2xl border border-brand-darkSage/5">
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-[9px] font-black uppercase tracking-widest text-brand-gold mb-1">Tracking Link</p>
                <p className="text-xs text-brand-darkSage font-mono truncate">{trackingLink}</p>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <button
                  onClick={() => onCopyLink(order.id)}
                  className="p-2.5 bg-white rounded-xl border border-brand-darkSage/10 text-brand-darkSage hover:border-brand-gold hover:text-brand-gold transition-all"
                  title="Copy link"
                >
                  <SafeIcon icon={copiedId === order.id ? FiCheck : FiCopy} size={14} />
                </button>
                <button
                  onClick={onViewTracking}
                  className="p-2.5 bg-brand-darkSage rounded-xl text-white hover:bg-brand-gold transition-all"
                  title="View tracking"
                >
                  <SafeIcon icon={FiExternalLink} size={14} />
                </button>
              </div>
            </div>
          </div>
        )}

        {isUnpaid && (
          <div className="mt-5 pt-5 border-t border-brand-darkSage/5 flex flex-wrap gap-3">
            <button 
              onClick={() => onResume(order)} 
              className="flex items-center gap-2 bg-brand-darkSage text-white px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-brand-gold transition-all shadow-lg"
            >
              <SafeIcon icon={FiRefreshCw} size={14} /> Complete Payment
            </button>
            <button 
              onClick={() => onCancel(order.id)} 
              className="flex items-center gap-2 border-2 border-red-100 text-red-500 px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-red-50 transition-all"
            >
              <SafeIcon icon={FiTrash2} size={14} /> Cancel Order
            </button>
          </div>
        )}

        {order.status === 'shipped' && order.tracking_number && (
          <div className="mt-5 pt-5 border-t border-brand-darkSage/5">
            <div className="bg-brand-gold/5 p-4 rounded-2xl border border-brand-gold/10 flex items-center justify-between">
              <div>
                <p className="text-[9px] font-black uppercase tracking-widest text-brand-gold mb-1">Tracking Number</p>
                <p className="font-mono font-bold text-brand-darkSage text-sm">{order.tracking_number}</p>
              </div>
              <SafeIcon icon={FiPackage} className="text-brand-gold w-6 h-6 opacity-30" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const TabButton = ({ active, onClick, icon: Icon, label, count }) => (
  <button 
    onClick={onClick}
    className={`flex items-center justify-between w-full px-5 py-4 rounded-2xl text-sm font-bold transition-all group ${
      active 
        ? 'bg-brand-darkSage text-white shadow-lg' 
        : 'text-brand-darkSage/60 hover:bg-white hover:text-brand-darkSage'
    }`}
  >
    <div className="flex items-center gap-3">
      <Icon size={20} className={active ? 'text-brand-gold' : 'text-brand-darkSage/40 group-hover:text-brand-gold transition-colors'} />
      {label}
    </div>
    <div className="flex items-center gap-2">
      {count !== undefined && (
        <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${active ? 'bg-white/20 text-white' : 'bg-brand-sand text-brand-lightText'}`}>
          {count}
        </span>
      )}
      <SafeIcon icon={FiChevronRight} size={16} className={`transition-transform duration-300 ${active ? 'opacity-100 rotate-90' : 'opacity-0 -translate-x-2'}`} />
    </div>
  </button>
);

export default Profile;